import React, { useEffect, useState } from "react";
import { fetchFileHistory } from "../api";
import { FileText, CheckCircle2, XCircle, Clock, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Search } from "lucide-react";
import "../pages/DataUploadPage.css"; // Reuse the beautiful table styles

const STATUS_CONFIG = {
  completed:  { label: "Completed",  color: "green",  icon: <CheckCircle2 size={13} /> },
  failed:     { label: "Failed",     color: "red",    icon: <XCircle size={13} /> },
  processing: { label: "Processing", color: "amber",  icon: <RefreshCw size={13} className="animate-spin" /> },
  pending:    { label: "Pending",    color: "gray",   icon: <Clock size={13} /> },
};

export default function FileHistory({ refreshTrigger }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchFileHistory(null);
      // The backend returns paginated data: { items: [...], next_token: ... }
      setLogs(Array.isArray(data) ? data : (data?.items || []));
    } catch (err) {
      console.error("Failed to load file history", err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const filteredLogs = logs.filter((h) => {
    if (filterStatus !== "all" && h.status !== filterStatus) return false;
    if (filterType !== "all" && h.fileType !== filterType) return false;
    if (searchQuery && !(h.fileName || "").toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="history-section" style={{ marginTop: 0, padding: 0, border: 'none' }}>
      <div className="section-header">
        <h2 className="section-title">Processing History</h2>
        <div className="history-filters">
          <div className="search-wrap">
            <Search size={14} className="search-icon" />
            <input
              className="search-input"
              placeholder="Search files…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All types</option>
            <option value="customers">Customers</option>
            <option value="sales">Sales</option>
          </select>
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="completed">Completed</option>
            <option value="processing">Processing</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          <button
            onClick={load}
            className="btn-ghost btn-sm"
            style={{ padding: "0 12px" }}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {filteredLogs.length === 0 ? (
        <div className="history-empty">
          <FileText size={32} />
          <span>{loading ? "Loading history..." : "No files match your filters"}</span>
        </div>
      ) : (
        <div className="history-table-wrap">
          <table className="history-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Records</th>
                <th>Uploaded</th>
                <th>By</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((row) => {
                const sc = STATUS_CONFIG[row.status] || STATUS_CONFIG.pending;
                const isExpanded = expandedRow === row.fileId;
                return (
                  <React.Fragment key={row.fileId}>
                    <tr
                      className={`history-row ${isExpanded ? "history-row-expanded" : ""}`}
                      onClick={() => setExpandedRow(isExpanded ? null : row.fileId)}
                    >
                      <td>
                        <div className="history-filename">
                          <FileText size={14} className="history-file-icon" />
                          <span title={row.fileId}>{row.fileName}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${row.fileType === "customers" ? "blue" : "green"}`}>
                          {row.fileType}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge status-${sc.color}`}>
                          {sc.icon} {sc.label}
                        </span>
                      </td>
                      <td>
                        {row.recordsTotal != null ? (
                          <div className="records-cell">
                            <span className="records-inserted">{row.recordsInserted?.toLocaleString() ?? "0"}</span>
                            <span className="records-sep">/</span>
                            <span className="records-total">{row.recordsTotal.toLocaleString()}</span>
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-muted text-sm">
                        {row.uploadedAt ? new Date(row.uploadedAt).toLocaleString() : "—"}
                      </td>
                      <td>
                        <span className="history-user">{row.uploadedBy}</span>
                      </td>
                      <td>
                        <button className="expand-btn" aria-label="Expand row">
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="history-detail-row animate-fade">
                        <td colSpan={7}>
                          <div className="history-detail">
                            <div className="detail-grid">
                              {[
                                { label: "File Name",        value: row.fileName },
                                { label: "File Type",        value: row.fileType },
                                { label: "Status",           value: row.status },
                                { label: "Total Records",    value: row.recordsTotal?.toLocaleString() ?? "—" },
                                { label: "Inserted",         value: row.recordsInserted?.toLocaleString() ?? "0" },
                                { label: "Skipped (Dupes)",  value: row.recordsSkipped?.toLocaleString() ?? "0" },
                                { label: "Uploaded At",      value: row.uploadedAt ? new Date(row.uploadedAt).toLocaleString() : "—" },
                                { label: "Processed At",     value: row.processedAt ? new Date(row.processedAt).toLocaleString() : "—" },
                                { label: "Uploaded By",      value: row.uploadedBy },
                              ].map((d) => (
                                <div key={d.label} className="detail-item">
                                  <span className="detail-label">{d.label}</span>
                                  <span className="detail-value">{d.value}</span>
                                </div>
                              ))}
                            </div>
                            {row.errorMessage && (
                              <div className="detail-error">
                                <AlertTriangle size={14} />
                                <span>{row.errorMessage}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
